import React, { useState, useEffect } from "react";

const TimeDurationCalculator = () => {
  const [startHours, setStartHours] = useState(9);
  const [startMinutes, setStartMinutes] = useState(0);
  const [startSeconds, setStartSeconds] = useState(0);
  const [endHours, setEndHours] = useState(17);
  const [endMinutes, setEndMinutes] = useState(30);
  const [endSeconds, setEndSeconds] = useState(0);
  const [calculationType, setCalculationType] = useState("duration");
  const [addHours, setAddHours] = useState(2);
  const [addMinutes, setAddMinutes] = useState(30);
  const [addSeconds, setAddSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(3600);
  const [showSteps, setShowSteps] = useState("yes");
  const [result, setResult] = useState({
    duration: "0:00:00",
    category: "",
    interpretation: "",
    advice: "",
    totalMinutes: "0",
    totalHours: "0",
    totalSeconds: "0",
  });

  // Time Duration Calculation
  const calculateTimeDuration = () => {
    if (calculationType === "duration") {
      // Calculate duration between two times
      const startTotal = parseInt(startHours) * 3600 + parseInt(startMinutes) * 60 + parseInt(startSeconds);
      const endTotal = parseInt(endHours) * 3600 + parseInt(endMinutes) * 60 + parseInt(endSeconds);
      
      let durationSeconds = endTotal - startTotal;
      if (durationSeconds < 0) {
        durationSeconds += 24 * 3600; // Handle crossing midnight
      }

      const hours = Math.floor(durationSeconds / 3600);
      const minutes = Math.floor((durationSeconds % 3600) / 60);
      const seconds = durationSeconds % 60;

      const totalMinutes = (durationSeconds / 60).toFixed(2);
      const totalHours = (durationSeconds / 3600).toFixed(2);

      // Determine category and interpretation
      let category = "";
      let interpretation = "";

      if (durationSeconds === 0) {
        category = "No Duration";
        interpretation = "The start and end times are identical. There is no time difference.";
      } else if (durationSeconds < 1800) { // Less than 30 minutes
        category = "Short Duration";
        interpretation = `A duration of ${hours}h ${minutes}m ${seconds}s is brief. This is suitable for quick tasks, breaks, or short meetings.`;
      } else if (durationSeconds < 3600) { // Less than 1 hour
        category = "Medium Duration";
        interpretation = `A duration of ${hours}h ${minutes}m ${seconds}s represents a moderate time span. Good for focused work sessions or standard meetings.`;
      } else if (durationSeconds < 14400) { // Less than 4 hours
        category = "Extended Duration";
        interpretation = `A duration of ${hours}h ${minutes}m ${seconds}s is a substantial time block. Ideal for deep work, workshops, or major tasks.`;
      } else if (durationSeconds < 28800) { // Less than 8 hours
        category = "Long Duration";
        interpretation = `A duration of ${hours}h ${minutes}m ${seconds}s represents a significant time commitment. This is comparable to a standard work day or shift.`;
      } else {
        category = "Very Long Duration";
        interpretation = `A duration of ${hours}h ${minutes}m ${seconds}s is an extended period. This exceeds a typical work day and may span multiple shifts or activities.`;
      }

      // Generate advice
      let advice = "";
      if (durationSeconds < 3600) {
        advice = "Short durations are perfect for focused sprints. Use techniques like Pomodoro for maximum productivity. Take breaks to maintain concentration.";
      } else if (durationSeconds < 14400) {
        advice = "For durations of this length, plan breaks every 60-90 minutes. Structure your time with clear objectives and milestones to maintain momentum.";
      } else if (durationSeconds < 28800) {
        advice = "Long durations require strategic planning. Include multiple breaks, vary your activities, and monitor energy levels. Consider splitting complex tasks across this timeframe.";
      } else {
        advice = "Extended durations spanning multiple hours require careful time management. Plan rest periods, meals, and activity changes. Track progress regularly and adjust plans as needed.";
      }

      setResult({
        duration: `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
        category,
        interpretation,
        advice,
        totalMinutes,
        totalHours,
        totalSeconds: durationSeconds.toString(),
        startTime: `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}:${startSeconds.toString().padStart(2, '0')}`,
        endTime: `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}:${endSeconds.toString().padStart(2, '0')}`,
      });

    } else if (calculationType === "add") {
      // Add time to a starting time
      const startTotal = parseInt(startHours) * 3600 + parseInt(startMinutes) * 60 + parseInt(startSeconds);
      const addTotal = parseInt(addHours) * 3600 + parseInt(addMinutes) * 60 + parseInt(addSeconds);
      
      let endTotal = startTotal + addTotal;
      endTotal = endTotal % (24 * 3600); // Wrap around 24 hours

      const endH = Math.floor(endTotal / 3600);
      const endM = Math.floor((endTotal % 3600) / 60);
      const endS = endTotal % 60;

      setResult({
        duration: `${addHours}:${addMinutes.toString().padStart(2, '0')}:${addSeconds.toString().padStart(2, '0')}`,
        category: "Time Addition",
        interpretation: `Adding ${addHours}h ${addMinutes}m ${addSeconds}s to ${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}:${startSeconds.toString().padStart(2, '0')} results in ${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}:${endS.toString().padStart(2, '0')}.`,
        advice: "This calculation helps you plan end times for activities. Useful for scheduling meetings, appointments, or estimating completion times.",
        totalMinutes: (addTotal / 60).toFixed(2),
        totalHours: (addTotal / 3600).toFixed(2),
        totalSeconds: addTotal.toString(),
        startTime: `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}:${startSeconds.toString().padStart(2, '0')}`,
        endTime: `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}:${endS.toString().padStart(2, '0')}`,
      });

    } else if (calculationType === "subtract") {
      // Subtract time from a starting time
      const startTotal = parseInt(startHours) * 3600 + parseInt(startMinutes) * 60 + parseInt(startSeconds);
      const subTotal = parseInt(addHours) * 3600 + parseInt(addMinutes) * 60 + parseInt(addSeconds);
      
      let endTotal = startTotal - subTotal;
      if (endTotal < 0) endTotal += 24 * 3600; // Wrap around

      const endH = Math.floor(endTotal / 3600);
      const endM = Math.floor((endTotal % 3600) / 60);
      const endS = endTotal % 60;

      setResult({
        duration: `${addHours}:${addMinutes.toString().padStart(2, '0')}:${addSeconds.toString().padStart(2, '0')}`,
        category: "Time Subtraction",
        interpretation: `Subtracting ${addHours}h ${addMinutes}m ${addSeconds}s from ${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}:${startSeconds.toString().padStart(2, '0')} results in ${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}:${endS.toString().padStart(2, '0')}.`,
        advice: "This calculation helps you determine when to start an activity to finish by a specific time. Useful for backward planning and deadline management.",
        totalMinutes: (subTotal / 60).toFixed(2),
        totalHours: (subTotal / 3600).toFixed(2),
        totalSeconds: subTotal.toString(),
        startTime: `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}:${startSeconds.toString().padStart(2, '0')}`,
        endTime: `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}:${endS.toString().padStart(2, '0')}`,
      });

    } else if (calculationType === "convert") {
      // Convert total seconds to time format
      const seconds = parseInt(totalSeconds);
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      setResult({
        duration: `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`,
        category: "Time Conversion",
        interpretation: `${seconds} seconds equals ${hours} hours, ${minutes} minutes, and ${secs} seconds.`,
        advice: "This conversion helps translate raw time measurements into readable formats. Useful for interpreting timestamps, durations, and time-based data.",
        totalMinutes: (seconds / 60).toFixed(2),
        totalHours: (seconds / 3600).toFixed(2),
        totalSeconds: seconds.toString(),
        startTime: "00:00:00",
        endTime: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`,
      });
    }
  };

  useEffect(() => {
  calculateTimeDuration();
}, [startHours, startMinutes, startSeconds, endHours, endMinutes, endSeconds, addHours, addMinutes, addSeconds, totalSeconds, calculationType]);


  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <h1>Time Duration Calculator</h1>
          <p>
            Calculate time durations, add or subtract time intervals, and convert between time formats. A versatile tool for scheduling, time tracking, project planning, and analyzing time-based data.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Calculation Details
              </h2>

              {/* Calculation Type */}
              <div className="input-group">
                <label>Calculation Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "duration", label: "Duration" },
                    { value: "add", label: "Add Time" },
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCalculationType(type.value)}
                      className={`quick-select-btn ${calculationType === type.value ? "primary" : "secondary"}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{ marginTop: "0.5rem" }}>
                  {[
                    { value: "subtract", label: "Subtract Time" },
                    { value: "convert", label: "Convert" },
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCalculationType(type.value)}
                      className={`quick-select-btn ${calculationType === type.value ? "primary" : "secondary"}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {(calculationType === "duration" || calculationType === "add" || calculationType === "subtract") && (
                <>
                  {/* Start Time */}
                  <div className="input-group">
                    <label>Start Time</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">H</span>
                          <input
                            type="number"
                            value={startHours}
                            onChange={(e) => setStartHours(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                            className="calc-input"
                            placeholder="9"
                            min="0"
                            max="23"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">M</span>
                          <input
                            type="number"
                            value={startMinutes}
                            onChange={(e) => setStartMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                            className="calc-input"
                            placeholder="0"
                            min="0"
                            max="59"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">S</span>
                          <input
                            type="number"
                            value={startSeconds}
                            onChange={(e) => setStartSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                            className="calc-input"
                            placeholder="0"
                            min="0"
                            max="59"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="quick-select-group">
                      {[
                        { h: 9, m: 0, label: "9:00" },
                        { h: 12, m: 0, label: "12:00" },
                        { h: 17, m: 0, label: "17:00" },
                      ].map((time) => (
                        <button
                          key={time.label}
                          onClick={() => { setStartHours(time.h); setStartMinutes(time.m); setStartSeconds(0); }}
                          className="quick-select-btn primary"
                        >
                          {time.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {calculationType === "duration" && (
                <>
                  {/* End Time */}
                  <div className="input-group">
                    <label>End Time</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">H</span>
                          <input
                            type="number"
                            value={endHours}
                            onChange={(e) => setEndHours(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                            className="calc-input"
                            placeholder="17"
                            min="0"
                            max="23"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">M</span>
                          <input
                            type="number"
                            value={endMinutes}
                            onChange={(e) => setEndMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                            className="calc-input"
                            placeholder="30"
                            min="0"
                            max="59"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">S</span>
                          <input
                            type="number"
                            value={endSeconds}
                            onChange={(e) => setEndSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                            className="calc-input"
                            placeholder="0"
                            min="0"
                            max="59"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="quick-select-group">
                      {[
                        { h: 12, m: 0, label: "12:00" },
                        { h: 17, m: 30, label: "17:30" },
                        { h: 23, m: 59, label: "23:59" },
                      ].map((time) => (
                        <button
                          key={time.label}
                          onClick={() => { setEndHours(time.h); setEndMinutes(time.m); setEndSeconds(0); }}
                          className="quick-select-btn primary"
                        >
                          {time.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {(calculationType === "add" || calculationType === "subtract") && (
                <>
                  {/* Time to Add/Subtract */}
                  <div className="input-group">
                    <label>{calculationType === "add" ? "Time to Add" : "Time to Subtract"}</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">H</span>
                          <input
                            type="number"
                            value={addHours}
                            onChange={(e) => setAddHours(Math.max(0, parseInt(e.target.value) || 0))}
                            className="calc-input"
                            placeholder="2"
                            min="0"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">M</span>
                          <input
                            type="number"
                            value={addMinutes}
                            onChange={(e) => setAddMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                            className="calc-input"
                            placeholder="30"
                            min="0"
                            max="59"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="input-wrapper">
                          <span className="input-prefix">S</span>
                          <input
                            type="number"
                            value={addSeconds}
                            onChange={(e) => setAddSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                            className="calc-input"
                            placeholder="0"
                            min="0"
                            max="59"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="quick-select-group">
                      {[
                        { h: 1, m: 0, label: "1h" },
                        { h: 2, m: 30, label: "2h 30m" },
                        { h: 8, m: 0, label: "8h" },
                      ].map((time) => (
                        <button
                          key={time.label}
                          onClick={() => { setAddHours(time.h); setAddMinutes(time.m); setAddSeconds(0); }}
                          className="quick-select-btn success"
                        >
                          {time.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {calculationType === "convert" && (
                <>
                  {/* Total Seconds */}
                  <div className="input-group">
                    <label>Total Seconds</label>
                    <div className="input-wrapper">
                      <span className="input-prefix">⏱️</span>
                      <input
                        type="number"
                        value={totalSeconds}
                        onChange={(e) => setTotalSeconds(Math.max(0, parseInt(e.target.value) || 0))}
                        className="calc-input"
                        placeholder="3600"
                        min="0"
                      />
                    </div>
                    <div className="quick-select-group">
                      {[
                        { val: 3600, label: "1 hour" },
                        { val: 7200, label: "2 hours" },
                        { val: 86400, label: "1 day" },
                      ].map((time) => (
                        <button
                          key={time.label}
                          onClick={() => setTotalSeconds(time.val)}
                          className="quick-select-btn primary"
                        >
                          {time.label}
                        </button>
                      ))}
                    </div>
                    <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                      Enter the number of seconds to convert to hours:minutes:seconds format.
                    </p>
                  </div>
                </>
              )}

              {/* Show Steps */}
              <div className="input-group">
                <label>Show Calculation Details</label>
                <div className="quick-select-group">
                  {[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setShowSteps(option.value)}
                      className={`quick-select-btn ${showSteps === option.value ? "primary" : "secondary"}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateTimeDuration} className="calculate-btn">
                Calculate Duration
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Calculation Results
              </h2>

              <div className="result-card">
                {/* Duration Display */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Duration</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.duration}</div>
                  <div className="result-subtext">{result.category}</div>
                </div>

                {/* Time Breakdown */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Start Time</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 11 12 14 22 4"></polyline>
                          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.startTime}</div>
                    <div className="result-subtext">beginning</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">End Time</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.5rem" }}>{result.endTime}</div>
                    <div className="result-subtext">completion</div>
                  </div>
                </div>

                {/* Additional Metrics */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginTop: "1rem" }}>
                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Total Hours</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.25rem" }}>{result.totalHours}</div>
                    <div className="result-subtext">hours</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Total Minutes</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12"></polyline>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.25rem" }}>{result.totalMinutes}</div>
                    <div className="result-subtext">minutes</div>
                  </div>

                  <div className="result-item">
                    <div className="result-item-header">
                      <span className="result-label">Total Seconds</span>
                      <div className="result-icon-wrapper green">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="12" x2="12" y2="6"></line>
                        </svg>
                      </div>
                    </div>
                    <div className="result-value" style={{ fontSize: "1.25rem" }}>{result.totalSeconds}</div>
                    <div className="result-subtext">seconds</div>
                  </div>
                </div>

                {/* Interpretation */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">What This Means</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.advice}
                  </p>
                </div>

                {/* Duration Categories */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">Duration Categories</h3>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#f3f4f6", borderRadius: "0.375rem", borderLeft: "4px solid #6b7280" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>No Duration: 0 seconds</div>
                    <div style={{ fontSize: "0.75rem", color: "#374151" }}>No time difference</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#dbeafe", borderRadius: "0.375rem", borderLeft: "4px solid #3b82f6" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Short: 0-30 minutes</div>
                    <div style={{ fontSize: "0.75rem", color: "#1e40af" }}>Quick tasks and breaks</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#bfdbfe", borderRadius: "0.375rem", borderLeft: "4px solid #2563eb" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Medium: 30-60 minutes</div>
                    <div style={{ fontSize: "0.75rem", color: "#1e40af" }}>Focused work sessions</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fef3c7", borderRadius: "0.375rem", borderLeft: "4px solid #f59e0b" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Extended: 1-4 hours</div>
                    <div style={{ fontSize: "0.75rem", color: "#92400e" }}>Deep work blocks</div>
                  </div>
                  <div style={{ marginBottom: "0.5rem", padding: "0.75rem", backgroundColor: "#fed7aa", borderRadius: "0.375rem", borderLeft: "4px solid #ea580c" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Long: 4-8 hours</div>
                    <div style={{ fontSize: "0.75rem", color: "#92400e" }}>Work day duration</div>
                  </div>
                  <div style={{ padding: "0.75rem", backgroundColor: "#fecaca", borderRadius: "0.375rem", borderLeft: "4px solid #ef4444" }}>
                    <div style={{ fontWeight: "600", fontSize: "0.85rem" }}>Very Long: 8+ hours</div>
                    <div style={{ fontSize: "0.75rem", color: "#991b1b" }}>Extended periods</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Time Duration</h3>
              <p>
                Time duration calculation is essential for scheduling, project management, time tracking, and productivity analysis. It helps you understand how long activities take, plan your schedule effectively, and manage time-based resources.
                Whether you're tracking work hours, planning meetings, calculating travel time, or analyzing project timelines, accurate time duration calculations enable better decision-making and resource allocation.
                Understanding time calculations helps optimize schedules, improve time management, set realistic deadlines, and measure productivity across personal and professional contexts.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Time Calculation Formulas:</h4>
            <div className="formula-display">
              Duration = End Time - Start Time
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              End Time = Start Time + Duration
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Start Time = End Time - Duration
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Total Seconds = (Hours × 3600) + (Minutes × 60) + Seconds
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Hours</div>
                <div className="formula-param-value">0-23 (24-hour format)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Minutes</div>
                <div className="formula-param-value">0-59</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Seconds</div>
                <div className="formula-param-value">0-59</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Duration</div>
                <div className="formula-param-value">Time difference</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How do I calculate duration across midnight?</h5>
              <p>
                When the end time is earlier than the start time (e.g., 23:00 to 02:00), the calculator automatically adds 24 hours to account for crossing midnight. This gives you the correct duration of 3 hours instead of a negative value.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between elapsed time and duration?</h5>
              <p>
                These terms are often used interchangeably. Both refer to the amount of time that passes between two points. Duration typically refers to how long something lasts, while elapsed time emphasizes the passage of time between start and end points.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I convert decimal hours to hours and minutes?</h5>
              <p>
                To convert decimal hours (e.g., 2.5 hours) to hours and minutes: Take the whole number as hours (2), multiply the decimal by 60 for minutes (0.5 × 60 = 30). So 2.5 hours = 2 hours 30 minutes. Our calculator displays both formats for convenience.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I add multiple time durations together?</h5>
              <p>
                To add multiple durations, convert each to total seconds, add them together, then convert back to hours:minutes:seconds format. For example: 1:30:00 (5400s) + 2:15:00 (8100s) = 13,500 seconds = 3:45:00.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the best way to track time for billing?</h5>
              <p>
                For billing purposes, track start and end times precisely, round to your billing increment (e.g., 15 minutes, 6 minutes), and maintain detailed records. Many professionals use 6-minute increments (0.1 hours) for legal billing or 15-minute increments for consulting.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate time zones differences?</h5>
              <p>
                To calculate time zone differences, convert both times to a common reference (like UTC), then calculate the duration. For example, if it's 14:00 EST (19:00 UTC) and you need to know when it's 09:00 PST (17:00 UTC), the duration is 2 hours earlier.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeDurationCalculator;